import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import Info from '@mui/icons-material/Info';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { Tooltip, CircularProgress } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { Box, Typography } from '@mui/material';
import { generateInvoice } from './InvoicePrint';
import { generateMealInvoice } from './InvoiceMealPrint';
import styles from './Invoice.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './InvoiceGenerate.css';
import ResponsiveTable from '../../ResponsiveTable';

function InvoiceIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [invoiceChildDetails, setInvoiceChildDetails] = useState(null);
    const [modalExpandedCategories, setModalExpandedCategories] = useState({});

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');
    const location = useLocation();

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/InvoiceCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/InvoiceEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/InvoiceDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    useEffect(() => {
        // // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data

        fetchTableData();
    }, [isAuthenticated, companyId, branchId, fromDate, toDate]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    // Function to fetch table data and set state
    const fetchTableData = async () => {
        setData([]);
        setLoading(true);
        fetchData('InvoiceApi', `/InvoiceDetails/${companyId}/${companyId}/${fromDate}/${toDate}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const handleDelete = async () => {
        deleteData('InvoiceApi', `${deleteItemId}`, token)//, token
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('InvoiceApi', `${itemId}`)//, token
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handlePrintInvoice = async (saleId) => {
        try {
            await generateInvoice(saleId, token);
        } catch (error) {
            console.error('Error printing invoice:', error);
        }
    };

    const handlePrintMealInvoice = async (saleId) => {
        try {
            await generateMealInvoice(saleId, token);
        } catch (error) {
            console.error('Error printing meal invoice:', error);
        }
    };

    const handleViewDetails = async (invoiceId) => {
        try {
            setLoading(true);
            const response = await fetchData('InvoiceApi', `/${invoiceId}`, token);
            if (response.responseCode === '1111') {
                setInvoiceDetails(response.response_data.Invoice);
                setInvoiceChildDetails(response.response_data.DailyFoodRegister || []);
                setShowInvoiceModal(true);
            }
        } catch (error) {
            console.error('Error fetching invoice details:', error);
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setShowInvoiceModal(false);
        setInvoiceDetails(null);
        setInvoiceChildDetails(null);
        setModalExpandedCategories({});
    };

    const groupByCategory = (data) => {
        const grouped = {};
        if (!Array.isArray(data)) {
            return grouped;
        }
        data.forEach(item => {
            const category = item.categoryName || 'Uncategorized';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(item);
        });
        return grouped;
    };

    const getCategoryTotal = (items) => {
        return items.reduce((sum, item) => sum + ((item.Rate || 0) * (item.Quantity || 0)), 0);
    };

    const toggleModalCategory = (categoryName) => {
        setModalExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    // Define table columns
    const columns = [
        {
            field: 'BillDate',
            header: 'Date',
            render: (row) => row.BillDate ? formatDate(row.BillDate) : "-"
        },
        {
            field: 'BillNumber',
            header: 'Invoice No',
            render: (row) => `${row.BillPrefix || ''}/${row.BillNumber || ''}/${row.FinYear}`
        },
        {
            field: 'InvoicePeriod',
            header: 'Invoice Period',
            render: (row) => `${row.InvoiceFromDate ? formatDate(row.InvoiceFromDate) : "-"} To ${row.InvoiceToDate ? formatDate(row.InvoiceToDate) : "-"}`
        },
        {
            field: 'CustomerName',
            header: 'Customer'
        },
        {
            field: 'Amount',
            header: 'Amount',
            render: (row) => row.Amount ? row.Amount.toFixed(2) : "0.00"
        },
        {
            field: 'TransportationCharges',
            header: 'Transportation',
            render: (row) => row.TransportationCharges ? row.TransportationCharges.toFixed(2) : "0.00"
        },
        {
            field: 'TotalAmount',
            header: 'Total',
            render: (row) => row.TotalAmount ? row.TotalAmount.toFixed(2) : "0.00"
        },
    ];

    // Define action buttons for each row
    const renderActions = (item) => (
        <Box className={styles.actionButtons}>
            <Tooltip title="Invoice Details" arrow>
                <IconButton
                    size="small"
                    onClick={() => handleViewDetails(item.Id)}
                    className={styles.iconButton}
                >
                    <Info sx={{ fontSize: 20, color: '#173B45' }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Print Invoice" arrow>
                <IconButton
                    size="small"
                    onClick={() => handlePrintInvoice(item.Id)}
                    className={styles.iconButton}
                >
                    <PrintIcon sx={{ fontSize: 18, color: '#173B45' }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Print Meal Invoice" arrow>
                <IconButton
                    size="small"
                    onClick={() => handlePrintMealInvoice(item.Id)}
                    className={styles.iconButton}
                >
                    <RestaurantIcon sx={{ fontSize: 18, color: '#173B45' }} />
                </IconButton>
            </Tooltip>

            <Tooltip
                title={item.IsActive ? 'Delete Invoice' : 'Activate Invoice'}
                arrow
            >
                <IconButton
                    size="small"
                    onClick={() =>
                        item.IsActive
                            ? openConfirmDialog(item.Id)
                            : handleActivate(item.Id)
                    }
                    className={styles.iconButton}
                    color="error"
                >
                    {item.IsActive ? (
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    ) : (
                        <ActivateIcon sx={{ fontSize: 18 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );

    return (
        <div className={styles.invoiceContainer}>
            <div className={styles.headerSection}>
                {/* <h5 className={styles.pageTitle}>Invoice</h5> */}
                <h6 className="block uppercase tracking-wide text-black productHead flex-grow linkcolor"
                    style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>
                    Invoice
                </h6>

                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingContainer}>
                            <div className={styles.loadingSpinner}>
                                <div className={styles.spinnerRing}></div>
                                <div className={styles.spinnerRing}></div>
                                <div className={styles.spinnerRing}></div>
                                <div className={styles.spinnerCore}></div>
                            </div>
                            <div className={styles.loadingTextWrapper}>
                                <span className={styles.loadingText}>Loading Invoices</span>
                                <span className={styles.loadingDots}>
                                    <span>.</span>
                                    <span>.</span>
                                    <span>.</span>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.searchContainer}>
                    <input
                        className={styles.searchInput}
                        type="text"
                        placeholder="Search invoices..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{ width: isFocused ? '270px' : '200px' }}
                    />

                    <div className={styles.dateFilterGroup}>
                        <div className={styles.dateInputWrapper}>
                            <label className={styles.dateLabel}>From Date</label>
                            <input
                                className={styles.dateInput}
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                            />
                        </div>

                        <div className={styles.dateInputWrapper}>
                            <label className={styles.dateLabel}>To Date</label>
                            <input
                                className={styles.dateInput}
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                            />
                        </div>

                        <Tooltip title="Refresh Invoice List" arrow>
                            <button className={styles.refreshButton} onClick={fetchTableData}>
                                Refresh
                            </button>
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div className={styles.watermark}>
                <CopyrightIcon />
                <b>EFFTABS SOLUTIONS LLP</b>
            </div>

            <div className={styles.tableWrapper}>
                <ResponsiveTable
                    columns={columns}
                    mobileColumns={['CustomerName', 'TotalAmount']}
                    mobileHeader={(row, index) => (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            width: '100%',
                            color: 'white'
                        }}>
                            <Typography sx={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: 'white'
                            }}>
                                {`${row.BillPrefix || ''}/${row.BillNumber || ''}/${row.FinYear}`}
                            </Typography>
                            <Typography sx={{
                                fontSize: '12px',
                                color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                                {row.BillDate ? formatDate(row.BillDate) : "-"}
                            </Typography>
                            {row.IsActive === false && (
                                <Box
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                                        color: 'white',
                                        px: 1.5,
                                        py: 0.3,
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        alignSelf: 'flex-start',
                                        mt: 0.5,
                                    }}
                                >
                                    Inactive
                                </Box>
                            )}
                        </Box>
                    )}
                    data={filteredData}
                    actions={renderActions}
                />
            </div>

            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    style: {
                        borderRadius: '12px',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle id="alert-dialog-title" style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                    Delete Invoice?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" style={{ color: '#555' }}>
                        Are you sure you want to delete this invoice? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>

                <DialogActions className={styles.dialogActions}>
                    <button onClick={closeConfirmDialog} className={styles.cancelButton}>
                        Cancel
                    </button>
                    <button onClick={handleDelete} className={styles.confirmButton}>
                        Confirm
                    </button>
                </DialogActions>
            </Dialog>

            {/* Invoice Details Modal */}
            {showInvoiceModal && invoiceDetails && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                📄 Invoice Details
                            </h3>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <CloseIcon />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="invoice-details-section">
                                <div className="invoice-info-grid">
                                    <div className="info-card">
                                        <div className="info-icon">📋</div>
                                        <div className="info-content">
                                            <span className="info-label">Invoice No</span>
                                            <span className="info-value">{invoiceDetails.BillPrefix}/{invoiceDetails.BillNumber}/{invoiceDetails.FinYear}</span>
                                        </div>
                                    </div>
                                    <div className="info-card">
                                        <div className="info-icon">📅</div>
                                        <div className="info-content">
                                            <span className="info-label">Invoice Date</span>
                                            <span className="info-value">{formatDate(invoiceDetails.BillDate)}</span>
                                        </div>
                                    </div>
                                    <div className="info-card">
                                        <div className="info-icon">👤</div>
                                        <div className="info-content">
                                            <span className="info-label">Customer</span>
                                            <span className="info-value">{invoiceDetails.CustomerName}</span>
                                        </div>
                                    </div>
                                    <div className="info-card">
                                        <div className="info-icon">📆</div>
                                        <div className="info-content">
                                            <span className="info-label">Period</span>
                                            <span className="info-value">
                                                {formatDate(invoiceDetails.InvoiceFromDate)} to {formatDate(invoiceDetails.InvoiceToDate)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-summary-grid">
                                    <div className="summary-card amount-card">
                                        <div className="summary-icon">💰</div>
                                        <div className="summary-content">
                                            <span className="summary-label">Amount</span>
                                            <span className="summary-value">₹ {invoiceDetails.Amount?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="summary-card transport-card">
                                        <div className="summary-icon">🚚</div>
                                        <div className="summary-content">
                                            <span className="summary-label">Transportation</span>
                                            <span className="summary-value">₹ {invoiceDetails.TransportationCharges?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="summary-card total-card">
                                        <div className="summary-icon">💳</div>
                                        <div className="summary-content">
                                            <span className="summary-label">Total Amount</span>
                                            <span className="summary-value total-amount">₹ {invoiceDetails.TotalAmount?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-table-container">
                                <table className="modal-invoice-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Item</th>
                                            <th>Qty</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceChildDetails && Object.entries(groupByCategory(invoiceChildDetails)).map(([categoryName, items], catIndex) => {
                                            const isExpanded = modalExpandedCategories[categoryName] !== false;
                                            return (
                                                <>
                                                    <tr
                                                        key={`modal-category-${catIndex}`}
                                                        className="modal-category-header-row"
                                                        onClick={() => toggleModalCategory(categoryName)}
                                                    >
                                                        <td colSpan="6" className="modal-category-header">
                                                            <div className="modal-category-header-content">
                                                                <span>{categoryName}</span>
                                                                <span className="modal-expand-icon">
                                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && items.map((item, index) => (
                                                        <tr key={`modal-${catIndex}-${index}`}>
                                                            <td>{formatDate(item.RegisterDate)}</td>
                                                            <td>{item.categoryName}</td>
                                                            <td>{item.ItemName}</td>
                                                            <td>{item.Quantity}</td>
                                                            <td>₹ {item.Rate?.toFixed(2)}</td>
                                                            <td>₹ {((item.Rate || 0) * (item.Quantity || 0)).toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                    <tr key={`modal-total-${catIndex}`} className="modal-category-total-row">
                                                        <td colSpan="5" className="modal-category-total-label">
                                                            {categoryName} Total
                                                        </td>
                                                        <td className="modal-category-total-value">
                                                            ₹ {getCategoryTotal(items).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="modal-total-row">
                                            <td colSpan="5" className="modal-total-label">Grand Total</td>
                                            <td className="modal-total-value">
                                                ₹ {invoiceDetails.TotalAmount?.toFixed(2)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button className="btn-modal-close" onClick={closeModal}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InvoiceIndex;

